// AddStaff.jsx - Fixed Component
import React, { useState } from 'react';
import Sidebar from '../Includes/Sidebar';
import GeneralService from '../../services/general.service';
import Footer from '../Includes/Footer';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

export default function AddStaff() {
    const [message, setMessage] = useState("");
    const [alertType, setAlertType] = useState(""); // 'success' or 'danger'
    const [isLoading, setIsLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const state = useSelector((state) => state.stateVals);
    const { id: adminId } = state;

    // ✅ Form Validation Schema
    const validationSchema = Yup.object({
        name: Yup.string().required("Name is required"),
        position: Yup.string().required("Position is required"),
        gender: Yup.string().required("Gender is required"),
        phone: Yup.string()
            .matches(/^[0-9]+$/, "Phone number must be digits only")
            .min(10, "Phone number must be at least 10 digits")
            .required("Phone number is required"),
        email: Yup.string().email("Invalid email format").nullable(),
        address: Yup.string().required("Address is required"),
        salary: Yup.number()
            .typeError("Salary must be a number")
            .min(0, "Salary cannot be negative")
            .nullable(),
        image: Yup.mixed().nullable(),
    });

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, resetForm, setFieldValue } =
        useFormik({
            initialValues: {
                name: '',
                position: '',
                gender: '', // ✅ Fixed: Changed from 'male' to empty string to force selection
                phone: '',
                email: '',
                address: '',
                salary: '',
                image: null
            },
            validationSchema,
            onSubmit: (values) => formSubmit(values),
        });

    // ✅ Handle Image Selection
    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // ✅ Added file type validation
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                setMessage("Please select a valid image file (JPEG, PNG, or GIF)");
                setAlertType("danger");
                return;
            }

            // ✅ Added file size validation (5MB limit)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                setMessage("Image size must be less than 5MB");
                setAlertType("danger");
                return;
            }

            setFieldValue('image', file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // ✅ Form Submission Function
const formSubmit = async (values) => {
    setIsLoading(true);
    setMessage("");

    try {
        const formData = new FormData();
        formData.append("name", values.name.trim());
        formData.append("position", values.position || "");
        formData.append("gender", values.gender || "");
        formData.append("phone", values.phone.trim());
        formData.append("email", values.email.trim() || "");
        formData.append("address", values.address.trim());
        formData.append("salary", values.salary || ""); // ✅ Send salary
        formData.append("admin_id", adminId);
        
        if (values.image) {
            formData.append("image", values.image);
        }

        // ✅ Call the service function
        const response = await GeneralService.addStaff(formData, adminId);
        
        // ✅ Check response format
        if (response.success) {
            setMessage(response.message || "Staff added successfully!");
            setAlertType("success");
            console.log('Created staff:', response.data);
        } else {
            setMessage(response.message || "Failed to add staff");
            setAlertType("danger");
        }

        // Reset form on success
        resetForm();
        setImagePreview(null);
        
        // Clear file input
        const fileInput = document.getElementById('image');
        if (fileInput) {
            fileInput.value = '';
        }
        
    } catch (err) {
        console.error("Add staff error:", err);
        setMessage(err.message || "Error adding staff. Please try again.");
        setAlertType("danger");
    }

    setIsLoading(false);
};

    return (
        <>
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h6 className="mb-0">Add Staff</h6>
                        </div>

                        {/* ✅ Success or Error Message */}
                        {message && (
                            <div className={`alert alert-${alertType} alert-dismissible fade show`} role="alert">
                                {message}
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    aria-label="Close"
                                    onClick={() => setMessage("")}
                                ></button>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate>
                            <div className='row'>
                                {/* Name */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-4">
                                        <input
                                            type="text"
                                            className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                                            id="name"
                                            name="name"
                                            placeholder="Name"
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        <label htmlFor="name">Name</label>
                                        {touched.name && errors.name && (
                                            <div className="invalid-feedback">{errors.name}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Position */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-4">
                                        <select
                                            className={`form-select ${touched.position && errors.position ? 'is-invalid' : ''}`}
                                            id="position"
                                            name="position"
                                            value={values.position}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        >
                                            <option value="">Select Position</option>
                                            <option value="General Manager">General Manager</option>
                                            <option value="Assistant Manager">Assistant Manager</option>
                                            <option value="Supervisor">Supervisor</option>
                                            <option value="Clerk">Clerk</option>
                                            <option value="Accountant">Accountant</option>
                                            <option value="Receptionist">Receptionist</option>
                                            <option value="Security Guard">Security Guard</option>
                                            <option value="Maintenance">Maintenance</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        <label htmlFor="position">Position</label>
                                        {touched.position && errors.position && (
                                            <div className="invalid-feedback">{errors.position}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Gender */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-4">
                                        <select
                                            className={`form-select ${touched.gender && errors.gender ? 'is-invalid' : ''}`}
                                            id="gender"
                                            name="gender"
                                            value={values.gender}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                        <label htmlFor="gender">Gender</label>
                                        {touched.gender && errors.gender && (
                                            <div className="invalid-feedback">{errors.gender}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Phone Number */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-4">
                                        <input
                                            type="text"
                                            className={`form-control ${touched.phone && errors.phone ? 'is-invalid' : ''}`}
                                            id="phone"
                                            name="phone"
                                            placeholder="Phone Number"
                                            value={values.phone}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        <label htmlFor="phone">Phone Number</label>
                                        {touched.phone && errors.phone && (
                                            <div className="invalid-feedback">{errors.phone}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Email */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-4">
                                        <input
                                            type="email"
                                            className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                                            id="email"
                                            name="email"
                                            placeholder="Email (Optional)"
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        <label htmlFor="email">Email (Optional)</label>
                                        {touched.email && errors.email && (
                                            <div className="invalid-feedback">{errors.email}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Salary */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-4">
                                        <input
                                            type="number"
                                            className={`form-control ${touched.salary && errors.salary ? 'is-invalid' : ''}`}
                                            id="salary"
                                            name="salary"
                                            placeholder="Salary (Optional)"
                                            min="0"
                                            value={values.salary}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        <label htmlFor="salary">Salary (Optional)</label>
                                        {touched.salary && errors.salary && (
                                            <div className="invalid-feedback">{errors.salary}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Address */}
                                <div className='col-md-12'>
                                    <div className="form-floating mb-4">
                                        <textarea
                                            className={`form-control ${touched.address && errors.address ? 'is-invalid' : ''}`}
                                            id="address"
                                            name="address"
                                            placeholder="Address"
                                            style={{ height: '100px' }}
                                            value={values.address}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        ></textarea>
                                        <label htmlFor="address">Address</label>
                                        {touched.address && errors.address && (
                                            <div className="invalid-feedback">{errors.address}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Image Upload */}
                                <div className='col-md-6'>
                                    <div className="mb-4">
                                        <label htmlFor="image" className="form-label">Staff Image (Optional)</label>
                                        <input
                                            type="file"
                                            className={`form-control ${touched.image && errors.image ? 'is-invalid' : ''}`}
                                            id="image"
                                            name="image"
                                            accept="image/jpeg,image/jpg,image/png,image/gif"
                                            onChange={handleImageChange}
                                        />
                                        <div className="form-text">Maximum file size: 5MB. Supported formats: JPEG, PNG, GIF</div>
                                        {touched.image && errors.image && (
                                            <div className="invalid-feedback">{errors.image}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Image Preview */}
                                {imagePreview && (
                                    <div className='col-md-6'>
                                        <div className="mb-4">
                                            <label className="form-label">Image Preview</label>
                                            <div className="text-center">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="img-thumbnail"
                                                    style={{ maxWidth: '200px', maxHeight: '200px' }}
                                                />
                                                <br />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger mt-2"
                                                    onClick={() => {
                                                        setImagePreview(null);
                                                        setFieldValue('image', null);
                                                        document.getElementById('image').value = '';
                                                    }}
                                                >
                                                    Remove Image
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Submit Button */}
                                <div className="col-md-12 d-flex">
                                    <button className="btn btn-submit" type="submit" disabled={isLoading}>
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Submitting...
                                            </>
                                        ) : (
                                            "Add Staff"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <Footer />
            </div>

            {/* ✅ Custom CSS */}
            <style jsx>{`
                .btn-submit {
                    background-color: #f44c00;
                    border-color: #f44c00;
                    padding: 14px 28px;
                    font-size: 16px;
                    font-weight: 600;
                    text-transform: uppercase;
                    border-radius: 30px;
                    transition: background-color 0.3s, border-color 0.3s;
                    display: inline-block;
                    margin-top: 10px;
                    color: #fff;
                }

                .btn-submit:hover {
                    background-color: #d43f00;
                    border-color: #d43f00;
                    color: #fff;
                }

                .btn-submit:focus {
                    outline: none;
                }

                .form-select {
                    padding-top: 1.625rem;
                    padding-bottom: 1.625rem;
                }

                .img-thumbnail {
                    border: 2px solid #f44c00;
                    border-radius: 10px;
                }
            `}</style>
        </>
    );
}